export type ReduxActionSummary = {
  type: string
  time: string
  // Small, sanitized hints only (no payload dumps)
  meta?: Record<string, unknown>
}

export type AuthType = 'auth0' | 'salesforce-canvas' | 'external-app'

export type UserTelemetryContext = {
  partnerId: string | null
  partnerUserId: string | null
  roles: string[]
  authType: AuthType
}

export type ReduxTelemetrySnapshot = {
  actions: ReduxActionSummary[]
  userContext: UserTelemetryContext
}

let getStateFn: (() => unknown) | undefined
let currentAuthType: AuthType = 'auth0'
const recentActions: ReduxActionSummary[] = []

const MAX_ACTIONS = 50
const MAX_KEYS_PER_OBJECT = 60
const MAX_ARRAY_ITEMS = 40
const MAX_STRING_CHARS = 300

const REDACT_KEY_RE =
  /(token|password|secret|authorization|jwt|cookie|session|csrf|license|api[_-]?key)/i

function clampString(s: string): string {
  return s.length > MAX_STRING_CHARS ? `${s.slice(0, MAX_STRING_CHARS)}…` : s
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return (
    typeof v === 'object' &&
    v !== null &&
    Object.getPrototypeOf(v) === Object.prototype
  )
}

function sanitizeValue(
  value: unknown,
  depth: number,
  keyHint?: string
): unknown {
  if (keyHint && REDACT_KEY_RE.test(keyHint)) return '[redacted]'
  if (depth <= 0) return '[truncated]'

  if (
    value === null ||
    value === undefined ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  )
    return value

  if (typeof value === 'string') return clampString(value)

  if (value instanceof Date) return value.toISOString()

  if (value instanceof Error) {
    return {
      name: value.name,
      message: clampString(value.message),
    }
  }

  if (Array.isArray(value)) {
    return value.slice(0, MAX_ARRAY_ITEMS).map(v => sanitizeValue(v, depth - 1))
  }

  if (isPlainObject(value)) {
    const out: Record<string, unknown> = {}
    const entries = Object.entries(value).slice(0, MAX_KEYS_PER_OBJECT)
    for (const [k, v] of entries) {
      out[k] = sanitizeValue(v, depth - 1, k)
    }
    return out
  }

  // Functions, class instances, bigints, symbols, etc.
  try {
    return clampString(String(value))
  } catch {
    return '[unserializable]'
  }
}

export function setReduxTelemetryStateGetter(getter: () => unknown): void {
  getStateFn = getter
}

export function setTelemetryAuthType(type: AuthType): void {
  currentAuthType = type
}

export function recordReduxAction(action: {
  type?: unknown
  meta?: unknown
}): void {
  const type = typeof action.type === 'string' ? action.type : 'unknown_action'
  const meta = isPlainObject(action.meta)
    ? (sanitizeValue(action.meta, 2) as Record<string, unknown>)
    : undefined

  recentActions.push({
    type,
    time: new Date().toISOString(),
    ...(meta ? { meta } : {}),
  })

  if (recentActions.length > MAX_ACTIONS) {
    recentActions.splice(0, recentActions.length - MAX_ACTIONS)
  }
}

function extractUserContext(rawState: unknown): UserTelemetryContext {
  const s = rawState as Record<string, any>
  const partnerId = s?.AccountData?.user?.partnerData?.partnerId ?? null
  const partnerUserId = s?.userProfile?.userProfile?.id ?? null
  const roles: string[] = Array.isArray(s?.userProfile?.userProfile?.roles)
    ? s.userProfile.userProfile.roles
    : []
  return { partnerId, partnerUserId, roles, authType: currentAuthType }
}

export function getReduxTelemetrySnapshot(): ReduxTelemetrySnapshot | null {
  if (!getStateFn) return null
  const rawState = getStateFn()
  const actions = recentActions.slice()
  const userContext = extractUserContext(rawState)

  return { actions, userContext }
}
